import React, { forwardRef, useState } from 'react';

import { Box, DialogActions, DialogContent, DialogTitle, IconButton, Dialog as MuiDialog, Theme, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SxProps } from '@mui/system';

import { X } from 'lucide-react';

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(4),
  top: theme.spacing(4),
  borderRadius: '50%',
  zIndex: 7,
}));

const HeroImage = styled('div')(({ theme }) => ({
  width: '100%',
}));

const Image = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(4, 4, 0, 0),
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const ContentWraper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

type DialogProps = {
  title: string;
  button?: React.ReactElement;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  sx?: SxProps<Theme> | undefined;
  titlePadding?: number;
  actions?: React.ReactElement;
  isOpen?: boolean;
  onClose?: () => void;
  imageHeader?: string;
  onKeyDown?: (event: any) => void;
};
export type DialogHandle = { setOpen: React.Dispatch<React.SetStateAction<boolean>>; close: () => void };

function Dialog(
  { title, button, maxWidth = 'sm', isOpen = false, onClose, titlePadding, actions, children, imageHeader, sx, ...props }: DialogProps,
  ref: any
) {
  const [open, setOpen] = useState(isOpen);
  const theme = useTheme();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };
  const handleClose = () => {
    if (onClose) onClose();
    setOpen(false);
  };
  React.useImperativeHandle(ref, () => ({ setOpen, close: handleClose }), []);
  return (
    <>
      {button &&
        React.cloneElement(button, {
          onClick: (event: any) => {
            if (button.props.onClick) button.props.onClick(event);
            handleOpen(event);
          },
        })}
      <MuiDialog
        sx={sx}
        fullWidth
        {...props}
        open={open}
        scroll="body"
        maxWidth={maxWidth}
        disableEscapeKeyDown={true}
        onClose={() => null}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <ContentWraper>
          {!!imageHeader ? (
            <HeroImage id="alert-dialog-title">
              <Image>
                <img src={imageHeader} alt="Logo" />
              </Image>
              <CloseButton aria-label="close" onClick={handleClose} sx={{ zIndex: 80 }}>
                <X />
              </CloseButton>
            </HeroImage>
          ) : (
            <DialogTitle id="alert-dialog-title">
              {title}
              <CloseButton aria-label="close" onClick={handleClose} sx={{ zIndex: 80 }}>
                <X />
              </CloseButton>
            </DialogTitle>
          )}
          <DialogContent>{children}</DialogContent>
          {actions && <DialogActions>{actions}</DialogActions>}
        </ContentWraper>
      </MuiDialog>
    </>
  );
}
export default forwardRef<DialogHandle, DialogProps>(Dialog);
